import { useState, useRef } from "react";
import PageSEO from "@/components/PageSEO";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Search, Zap, Check, ExternalLink, ArrowRight, Filter } from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41", 600: "#6e5c4e",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

function Reveal({ children, className, delay = 0, y = 25 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const VENDORS = [
  {
    brand: "Tata Power EV",
    hq: "Mumbai, Maharashtra",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 120 kW",
    priceRange: "₹45K – ₹18L",
    fame: true,
    listed: true,
    highlights: ["Largest deployed network in India", "CCS2 & Bharat DC001 connectors", "FAME-II & PM e-DRIVE eligible"],
    gradient: { from: "#1e3a5f", to: "#0f7dc2", accent: "#38bdf8" },
  },
  {
    brand: "Exicom",
    hq: "Gurugram, Haryana",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 60 kW",
    priceRange: "₹40K – ₹7L",
    fame: true,
    listed: true,
    highlights: ["Leading AC home charger OEM", "BIS certified products", "Fleet & residential solutions"],
    gradient: { from: "#0f2027", to: "#203a43", accent: "#2196F3" },
  },
  {
    brand: "ABB",
    hq: "Bengaluru, Karnataka",
    types: ["DC Fast", "HPC"],
    powerRange: "24 kW – 350 kW",
    priceRange: "₹5L – ₹45L",
    fame: false,
    listed: true,
    highlights: ["Global leader in fast chargers", "Terra series (highway & HPC)", "IEC 61851 & CHAdeMO certified"],
    gradient: { from: "#b71c1c", to: "#d32f2f", accent: "#ff5252" },
  },
  {
    brand: "Delta Electronics",
    hq: "Pune, Maharashtra",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 150 kW",
    priceRange: "₹38K – ₹16L",
    fame: true,
    listed: true,
    highlights: ["Industrial-grade reliability", "Smart energy management", "MoEFCC & CEA compliant"],
    gradient: { from: "#1a3a4a", to: "#0277BD", accent: "#4FC3F7" },
  },
  {
    brand: "Siemens",
    hq: "Mumbai, Maharashtra",
    types: ["DC Fast", "HPC"],
    powerRange: "22 kW – 200 kW",
    priceRange: "₹2L – ₹30L",
    fame: false,
    listed: true,
    highlights: ["SICHARGE series for enterprise", "Grid integration expertise", "SAP-connected billing support"],
    gradient: { from: "#0d5c8a", to: "#0077b6", accent: "#90e0ef" },
  },
  {
    brand: "Servotech",
    hq: "New Delhi",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 120 kW",
    priceRange: "₹35K – ₹13L",
    fame: true,
    listed: true,
    highlights: ["Made in India (PLI scheme)", "FAME-II listed", "AC & DC wall-mounted options"],
    gradient: { from: "#1b4332", to: "#2d6a4f", accent: "#74c69d" },
  },
  {
    brand: "Magenta Power",
    hq: "Mumbai, Maharashtra",
    types: ["AC", "DC Fast"],
    powerRange: "3.3 kW – 60 kW",
    priceRange: "₹18K – ₹6.5L",
    fame: true,
    listed: false,
    highlights: ["ChargeGrid network operator", "Residential & workplace AC", "FAME-II approved"],
    gradient: { from: "#4a004a", to: "#7b2d8b", accent: "#e040fb" },
  },
  {
    brand: "Okaya EV",
    hq: "New Delhi",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 60 kW",
    priceRange: "₹42K – ₹7.5L",
    fame: true,
    listed: false,
    highlights: ["Bharat DC001 compliant", "Competitive pricing", "Service network across 18 states"],
    gradient: { from: "#1a3a1a", to: "#2e7d32", accent: "#66bb6a" },
  },
  {
    brand: "Jio-BP",
    hq: "Mumbai, Maharashtra",
    types: ["DC Fast", "HPC"],
    powerRange: "30 kW – 150 kW",
    priceRange: "₹4L – ₹18L",
    fame: false,
    listed: false,
    highlights: ["Pulse series for highway hubs", "JBP + BP global technology", "Revenue-share partnerships"],
    gradient: { from: "#1e293b", to: "#0ea5e9", accent: "#7dd3fc" },
  },
  {
    brand: "Greaves Electric",
    hq: "Pune, Maharashtra",
    types: ["AC"],
    powerRange: "3.3 kW – 22 kW",
    priceRange: "₹18K – ₹1.5L",
    fame: true,
    listed: false,
    highlights: ["Ampere sub-brand chargers", "Compact wall-mount units", "Two-wheeler & four-wheeler ready"],
    gradient: { from: "#2c2c54", to: "#474787", accent: "#a29bfe" },
  },
  {
    brand: "ChargePoint",
    hq: "USA (India operations: Bengaluru)",
    types: ["DC Fast", "AC"],
    powerRange: "7.4 kW – 62 kW",
    priceRange: "₹1L – ₹12L",
    fame: false,
    listed: false,
    highlights: ["World's largest charging network", "CPaaS cloud-managed billing", "Enterprise & fleet focus"],
    gradient: { from: "#1a1a1a", to: "#333", accent: "#f97316" },
  },
  {
    brand: "Kazam EV",
    hq: "Bengaluru, Karnataka",
    types: ["AC"],
    powerRange: "3.3 kW – 7.4 kW",
    priceRange: "₹20K – ₹55K",
    fame: false,
    listed: false,
    highlights: ["Home & apartment charging", "App-based billing", "Compact & affordable"],
    gradient: { from: "#082f49", to: "#0284c7", accent: "#7dd3fc" },
  },
];

const TYPE_FILTERS = ["All", "DC Fast", "AC", "HPC"];

function VendorCard({ vendor, delay }) {
  const { brand, hq, types, powerRange, priceRange, fame, highlights, gradient } = vendor;
  return (
    <Reveal delay={delay}>
      <div
        className="rounded-2xl border overflow-hidden flex flex-col h-full"
        style={{ backgroundColor: "#fff", borderColor: F[200] }}
      >
        {/* Brand header */}
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})` }}
        >
          <div>
            <p className="font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>{brand}</p>
            <p className="text-xs" style={{ color: gradient.accent }}>{hq}</p>
          </div>
          {fame && (
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: gradient.accent + "30", color: gradient.accent, border: `1px solid ${gradient.accent}50` }}
            >
              FAME Eligible
            </span>
          )}
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col flex-1">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {types.map(t => (
              <span
                key={t}
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: F[50], color: F[500], border: `1px solid ${F[200]}` }}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-xs mb-0.5" style={{ color: F.m400 }}>Power range</p>
              <p className="text-sm font-semibold" style={{ color: F[800], fontFamily: "JetBrains Mono, monospace" }}>
                {powerRange}
              </p>
            </div>
            <div>
              <p className="text-xs mb-0.5" style={{ color: F.m400 }}>Price range</p>
              <p className="text-sm font-semibold" style={{ color: F[800], fontFamily: "JetBrains Mono, monospace" }}>
                {priceRange}
              </p>
            </div>
          </div>

          <ul className="space-y-1.5 flex-1">
            {highlights.map(h => (
              <li key={h} className="flex items-start gap-2 text-xs" style={{ color: F.m500 }}>
                <Check size={11} strokeWidth={3} style={{ color: F[500], flexShrink: 0, marginTop: 2 }} />
                {h}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
}

export default function Vendors() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [fameOnly, setFameOnly] = useState(false);

  const filtered = VENDORS.filter(v => {
    const matchSearch = v.brand.toLowerCase().includes(search.toLowerCase()) ||
      v.hq.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "All" || v.types.includes(typeFilter);
    const matchFame = !fameOnly || v.fame;
    return matchSearch && matchType && matchFame;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100, fontFamily: "Manrope, sans-serif" }}>
      <PageSEO
        title="EV Charging Equipment Vendors & OEMs in India"
        description="Directory of EV charging equipment vendors and OEMs in India. Compare Tata Power, ABB, Delta, Exicom, Greaves, Servotech and more — pricing, certifications and contact details."
        path="/vendors"
      />
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{ backgroundColor: F[800] + "f2", borderBottom: `1px solid ${F[700]}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <button
            onClick={() => navigate("/")}
            style={{ color: F[200], fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "1rem" }}
          >
            EV Hub Studio
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/charger-catalog")}
              className="text-sm hidden sm:block"
              style={{ color: F[300] }}
            >
              Charger Catalog
            </button>
            <button
              onClick={() => navigate("/try")}
              className="text-sm px-4 py-1.5 rounded-lg font-semibold"
              style={{ backgroundColor: F[500], color: "#fff" }}
            >
              Try Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 text-center">
        <Reveal>
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4"
            style={{ backgroundColor: F[500] + "20", color: F[500] }}
          >
            EV Charger Vendors — India
          </span>
          <h1
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            India's leading EV charger<br />manufacturers & OEMs
          </h1>
          <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: F.m400 }}>
            Compare {VENDORS.length} OEMs across power range, pricing, FAME eligibility, and charger types.
            Use the full planner to get AI-matched hardware recommendations for your specific site.
          </p>
        </Reveal>
      </section>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <Reveal>
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div
              className="relative flex-1 min-w-48 max-w-72"
            >
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: F.m400 }} />
              <input
                type="text"
                placeholder="Search brand or location…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: F[200], color: F[800], backgroundColor: "#fff" }}
              />
            </div>

            {/* Type filters */}
            <div className="flex gap-2 flex-wrap">
              {TYPE_FILTERS.map(t => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className="text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors"
                  style={{
                    backgroundColor: typeFilter === t ? F[500] : "#fff",
                    color: typeFilter === t ? "#fff" : F[800],
                    borderColor: typeFilter === t ? F[500] : F[200],
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* FAME toggle */}
            <button
              onClick={() => setFameOnly(f => !f)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors"
              style={{
                backgroundColor: fameOnly ? F[50] : "#fff",
                color: fameOnly ? F[500] : F[800],
                borderColor: fameOnly ? F[500] : F[200],
              }}
            >
              <Filter size={12} />
              FAME Eligible Only
            </button>

            <span className="text-sm ml-auto" style={{ color: F.m400 }}>
              {filtered.length} vendor{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </Reveal>
      </div>

      {/* Vendor grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {filtered.length === 0 ? (
          <div className="text-center py-16" style={{ color: F.m400 }}>
            No vendors match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((v, i) => (
              <VendorCard key={v.brand} vendor={v} delay={i * 0.04} />
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <section
        className="py-14"
        style={{ backgroundColor: F[900] }}
      >
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Reveal>
            <Zap size={28} className="mx-auto mb-3" style={{ color: F[400] }} />
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: F[200], fontFamily: "Chivo, sans-serif" }}
            >
              Need hardware recommendations for your site?
            </h2>
            <p className="text-sm mb-5" style={{ color: F[300] }}>
              The planner's AI matches charger models to your land size, power availability,
              expected traffic, and budget — and flags FAME-eligible options automatically.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => navigate("/try")}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90"
                style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
              >
                Try the Planner <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate("/charger-catalog")}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm border hover:opacity-80"
                style={{ borderColor: F[300], color: F[200], backgroundColor: "transparent" }}
              >
                Full Charger Catalog <ExternalLink size={15} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
